import type { FeatureCollection } from 'geojson';

import { apiClient } from './apiClient';
import { normaliseFeatureCollectionResponse } from './geoJsonUtils';
import type { NetworkStats, StatsPeriod } from './statsApi';

/**
 * Network Explorer feeds. Both span every client the estate exposes to the explorer, so
 * neither takes a client id — the server decides the row set from the caller's permission
 * and the per-client exclusion flag, never from a request parameter.
 */

/** The single-client stats shape plus the one figure only the estate-wide view has. */
export interface NetworkExplorerStats extends NetworkStats {
    /** Distinct clients (networks) that contributed at least one POI row. */
    totalNetworks: number;
}

/** Estate-wide statistics for the explorer header (GET /network-explorer/stats) over the chosen period. */
export async function fetchNetworkExplorerStats(
    period: StatsPeriod,
    signal?: AbortSignal,
): Promise<NetworkExplorerStats> {
    const response = await apiClient.get<NetworkExplorerStats>('/network-explorer/stats', {
        params: { period },
        signal,
    });
    return response.data;
}

/**
 * The estate's POI and leak activity pre-binned into grid cells (GET /network-explorer/clusters):
 * one point per cell at the mean entity position, carrying `count_total`, `count_pois`,
 * `count_leaks` and one `count_poi_<status>` / `count_leak_<status>` counter per presentation
 * status. The browser clusters these further; no individual record is ever downloaded.
 */
export async function fetchNetworkExplorerClusters(signal?: AbortSignal): Promise<FeatureCollection> {
    const response = await apiClient.get<unknown>('/network-explorer/clusters', { signal });
    return normaliseFeatureCollectionResponse(response.data);
}

/**
 * The individual POI and leak records inside a viewport across every included client
 * (GET /network-explorer/entities/{bbox}): the single-client viewport feed's features, plus
 * each record's `client_id` and `client_name`. Drawn from POI_MIN_ZOOM inward, where the
 * cells feed hands over.
 */
export async function fetchNetworkExplorerEntitiesForBounds(bbox: string): Promise<FeatureCollection> {
    const response = await apiClient.get<unknown>(`/network-explorer/entities/${encodeURIComponent(bbox)}`);
    return normaliseFeatureCollectionResponse(response.data);
}
